<template>
  <div class="message-center">
    <div class="message-header">
      <div class="header-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          <line x1="9" y1="9" x2="15" y2="9"/>
          <line x1="9" y1="13" x2="13" y2="13"/>
        </svg>
      </div>
      <h1>{{ isAdmin ? 'Üzenet Központ' : 'Tanár Üzenet Központ' }}</h1>
      <p>{{ isAdmin ? 'Küldj üzeneteket csoportoknak vagy egyéni táncosoknak' : 'Üzenet küldés a hozzárendelt csoportoknak' }}</p>
    </div>

    <div class="message-content">
      <!-- Message Type Selection -->
      <div class="message-types">
        <div class="type-tabs">
          <button 
            class="tab-button" 
            :class="{ active: messageType === 'group' }"
            @click="messageType = 'group'"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            Csoport Üzenet
          </button>
          <button 
            class="tab-button" 
            :class="{ active: messageType === 'child' }"
            @click="messageType = 'child'"
            v-if="isAdmin || isTeacher"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Egyéni Táncos
          </button>
          <button 
            class="tab-button" 
            :class="{ active: messageType === 'bulk' }"
            @click="messageType = 'bulk'"
            v-if="isAdmin"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6,9 6,2 18,2 18,9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Tömeges Üzenet
          </button>
        </div>
      </div>

      <!-- Group Message Form -->
      <div v-if="messageType === 'group'" class="message-form">
        <div class="form-group">
          <label>Válassz csoportot</label>
          <SearchableSelect
            v-model="selectedGroup"
            :options="groups"
            placeholder="Válassz egy csoportot..."
            search-placeholder="Keresés csoportok között..."
            :multiple="false"
            label-key="name"
            value-key="id"
          />
        </div>

        <div class="form-group">
          <label>Tárgy</label>
          <input 
            type="text" 
            v-model="subject" 
            placeholder="Üzenet tárgya"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label>Üzenet</label>
          <textarea 
            v-model="message" 
            placeholder="Írd be az üzenetet..."
            rows="6"
            class="form-textarea"
          ></textarea>
        </div>

        <div class="form-actions">
          <button 
            @click="sendGroupMessage" 
            :disabled="!canSendGroup || sending"
            class="btn btn-primary"
          >
            <span v-if="sending">Küldés...</span>
            <span v-else>Üzenet küldése</span>
          </button>
        </div>
      </div>

      <!-- Child Message Form -->
      <div v-if="messageType === 'child'" class="message-form">
        <div class="form-group">
          <label>Válassz táncost</label>
          <SearchableSelect
            v-model="selectedChild"
            :options="formattedDancers"
            placeholder="Válassz egy táncost..."
            search-placeholder="Keresés táncosok között..."
            :multiple="false"
            label-key="displayName"
            value-key="id"
          />
        </div>

        <div class="form-group">
          <label>Tárgy</label>
          <input 
            type="text" 
            v-model="subject" 
            placeholder="Üzenet tárgya"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label>Üzenet</label>
          <textarea 
            v-model="message" 
            placeholder="Írd be az üzenetet..."
            rows="6"
            class="form-textarea"
          ></textarea>
        </div>

        <div class="form-actions">
          <button 
            @click="sendChildMessage" 
            :disabled="!canSendChild || sending"
            class="btn btn-primary"
          >
            <span v-if="sending">Küldés...</span>
            <span v-else>Üzenet küldése</span>
          </button>
        </div>
      </div>

      <!-- Bulk Message Form -->
      <div v-if="messageType === 'bulk'" class="message-form">
        <div class="bulk-selection">
          <div class="form-group">
            <label>Csoportok kiválasztása</label>
            <SearchableSelect
              v-model="selectedGroups"
              :options="groups"
              placeholder="Válassz csoportokat..."
              search-placeholder="Keresés csoportok között..."
              :multiple="true"
              label-key="name"
              value-key="id"
            />
          </div>

          <div class="form-group">
            <label>Táncosok kiválasztása</label>
            <SearchableSelect
              v-model="selectedChildren"
              :options="formattedDancers"
              placeholder="Válassz táncosokat..."
              search-placeholder="Keresés táncosok között..."
              :multiple="true"
              label-key="displayName"
              value-key="id"
            />
          </div>
        </div>

        <div class="form-group">
          <label>Tárgy</label>
          <input 
            type="text" 
            v-model="subject" 
            placeholder="Üzenet tárgya"
            class="form-input"
          />
        </div>

        <div class="form-group">
          <label>Üzenet</label>
          <textarea 
            v-model="message" 
            placeholder="Írd be az üzenetet..."
            rows="6"
            class="form-textarea"
          ></textarea>
        </div>

        <div class="form-actions">
          <button 
            @click="sendBulkMessage" 
            :disabled="!canSendBulk || sending"
            class="btn btn-primary"
          >
            <span v-if="sending">Küldés...</span>
            <span v-else>Tömeges üzenet küldése</span>
          </button>
        </div>
      </div>

      <!-- Success/Error Messages -->
      <div v-if="successMessage" class="alert alert-success">
        {{ successMessage }}
      </div>

      <div v-if="errorMessage" class="alert alert-error">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import SearchableSelect from './shared/SearchableSelect.vue';

export default {
  name: 'MessageCenterItem',
  components: {
    SearchableSelect
  },
  computed: {
    ...mapGetters(['isAdmin', 'isTeacher']),
    
    canSendGroup() {
      return this.selectedGroup && this.subject.trim() && this.message.trim();
    },
    canSendChild() {
      return this.selectedChild && this.subject.trim() && this.message.trim();
    },
    canSendBulk() {
      return (this.selectedGroups.length > 0 || this.selectedChildren.length > 0) && 
             this.subject.trim() && this.message.trim();
    },
    
    formattedDancers() {
      return this.dancers.map(dancer => ({
        ...dancer,
        displayName: `${dancer.family_name} ${dancer.sure_name} (${dancer.group_name || 'Nincs csoport'})`
      }));
    }
  },
  data() {
    return {
      messageType: 'group',
      groups: [],
      dancers: [],
      selectedGroup: '',
      selectedChild: '',
      selectedGroups: [],
      selectedChildren: [],
      subject: '',
      message: '',
      sending: false,
      successMessage: '',
      errorMessage: ''
    };
  },
  watch: {
    selectedGroup(newVal, oldVal) {
      console.log('selectedGroup changed from:', oldVal, 'to:', newVal);
    },
    selectedChild(newVal, oldVal) {
      console.log('selectedChild changed from:', oldVal, 'to:', newVal);
    },
    selectedGroups(newVal, oldVal) {
      console.log('selectedGroups changed from:', oldVal, 'to:', newVal);
    },
    selectedChildren(newVal, oldVal) {
      console.log('selectedChildren changed from:', oldVal, 'to:', newVal);
    }
  },
  methods: {
    async loadGroups() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + '/api/message/groups');
        this.groups = response.data.groups || [];
      } catch (error) {
        console.error('Error loading groups:', error);
        this.errorMessage = 'Nem sikerült betölteni a csoportokat';
      }
    },

    async loadDancers() {
      try {
        const response = await axios.get(this.$BACKEND_HOST + '/api/message/dancers');
        this.dancers = response.data.dancers || [];
      } catch (error) {
        console.error('Error loading dancers:', error);
        this.errorMessage = 'Nem sikerült betölteni a táncosokat';
      }
    },

    async sendGroupMessage() {
      if (!this.canSendGroup) return;

      this.sending = true;
      this.clearMessages();

      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/message/group', {
          type: 'group',
          group_id: this.selectedGroup,
          subject: this.subject,
          message: this.message
        });

        this.successMessage = response.data.message + 
          ` (${response.data.recipients} címzett)`;
        this.resetForm();
      } catch (error) {
        console.error('Error sending group message:', error);
        this.errorMessage = error.response?.data?.message || 'Hiba történt az üzenet küldése során';
      } finally {
        this.sending = false;
      }
    },

    async sendChildMessage() {
      if (!this.canSendChild) return;

      this.sending = true;
      this.clearMessages();

      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/message/child', {
          type: 'child',
          child_id: this.selectedChild,
          subject: this.subject,
          message: this.message
        });

        this.successMessage = response.data.message + 
          ` (${response.data.recipients} címzett)`;
        this.resetForm();
      } catch (error) {
        console.error('Error sending child message:', error);
        this.errorMessage = error.response?.data?.message || 'Hiba történt az üzenet küldése során';
      } finally {
        this.sending = false;
      }
    },

    async sendBulkMessage() {
      if (!this.canSendBulk) return;

      this.sending = true;
      this.clearMessages();

      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/message/bulk', {
          group_ids: this.selectedGroups,
          child_ids: this.selectedChildren,
          subject: this.subject,
          message: this.message
        });

        this.successMessage = response.data.message + 
          ` (${response.data.recipients} egyedi címzett)`;
        this.resetForm();
      } catch (error) {
        console.error('Error sending bulk message:', error);
        this.errorMessage = error.response?.data?.message || 'Hiba történt az üzenet küldése során';
      } finally {
        this.sending = false;
      }
    },

    resetForm() {
      this.selectedGroup = '';
      this.selectedChild = '';
      this.selectedGroups = [];
      this.selectedChildren = [];
      this.subject = '';
      this.message = '';
    },

    clearMessages() {
      this.successMessage = '';
      this.errorMessage = '';
    }
  },
  mounted() {
    // Load real data from API
    this.loadGroups();
    if (this.isAdmin || this.isTeacher) {
      this.loadDancers();
    }
  },
  watch: {
    messageType() {
      this.clearMessages();
      this.resetForm();
    }
  }
};
</script>

<style scoped>
.message-center {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.message-header {
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  border-radius: 16px;
  padding: 2rem;
  color: white;
  text-align: center;
  box-shadow: var(--shadow-lg);
  margin-bottom: 2rem;
}

.header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  margin-bottom: 1rem;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.message-header h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.message-header p {
  font-size: 1rem;
  opacity: 0.9;
  margin: 0;
}

.message-content {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  padding: 2rem;
}

.message-types {
  margin-bottom: 2rem;
}

.type-tabs {
  display: flex;
  gap: 0.5rem;
  background: var(--color-background-mute);
  border-radius: 12px;
  padding: 0.5rem;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-weight: 500;
}

.tab-button:hover {
  background: var(--color-background);
  color: var(--color-text);
}

.tab-button.active {
  background: var(--primary-blue);
  color: white;
  box-shadow: var(--shadow-sm);
}

.message-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: var(--color-heading);
  font-size: 0.9rem;
}

.form-select,
.form-input,
.form-textarea {
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-text);
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.form-select:focus,
.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(var(--primary-blue-rgb), 0.1);
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
  background: var(--color-background-mute);
  border-radius: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--color-background);
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  font-weight: normal;
}

.checkbox-label:hover {
  background: var(--color-border);
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: var(--primary-blue);
}

.bulk-selection {
  background: var(--color-background-mute);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background: var(--primary-blue);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--secondary-blue);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn-primary:disabled {
  background: var(--color-text-muted);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.alert {
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-weight: 500;
}

.alert-success {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #15803d;
}

.alert-error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #dc2626;
}

/* Responsive design */
@media (max-width: 768px) {
  .message-center {
    padding: 1rem;
  }
  
  .message-header {
    padding: 1.5rem;
  }
  
  .message-content {
    padding: 1.5rem;
  }
  
  .type-tabs {
    flex-direction: column;
  }
  
  .tab-button {
    justify-content: center;
  }
}
</style>